import { useCallback } from "react";

import { SsnUpdaterDialogView } from "components/botsManagement/SsnUpdaterDialog";
import {
  useGetPdfJobSsnUpdaterQuery,
  useRunPdfJobSsnCheckMutation,
  useStartPdfJobSsnUpdaterMutation,
} from "features/pdfTools/pdfTools";

export interface SplitSsnUpdaterDialogProps {
  /** The saved split job; null keeps the dialog closed. */
  jobId: string | null;
  jobName: string;
  canEdit: boolean;
  readOnlyReason: string | null;
  onClose: () => void;
}

/**
 * SSN Updater of a saved split result (CAL-5987): the same dialog upload jobs
 * use, fed by the split job's SSN check. There is no destination yet - no
 * upload job is implied - so the "would match" count is not shown.
 */
export default function SplitSsnUpdaterDialog({
  jobId,
  jobName,
  canEdit,
  readOnlyReason,
  onClose,
}: SplitSsnUpdaterDialogProps) {
  const open = jobId != null;
  const status = useGetPdfJobSsnUpdaterQuery(
    { jobId: jobId ?? "", includeEntries: true },
    { skip: !open, refetchOnMountOrArgChange: true },
  );
  const data = status.currentData;
  const [recheck] = useRunPdfJobSsnCheckMutation();
  const [startUpdater, { isLoading: starting }] = useStartPdfJobSsnUpdaterMutation();
  const { refetch } = status;

  // A read-only viewer never starts a check, not even the one on open.
  const handleRecheck = useCallback(() => {
    if (jobId && canEdit) void recheck(jobId);
  }, [jobId, canEdit, recheck]);
  const handleStart = useCallback(
    async (allowSimilar: boolean) => {
      const response = await startUpdater({ jobId: jobId ?? "", allowSimilar }).unwrap();
      return response.run.run_id;
    },
    [jobId, startUpdater],
  );
  const handleRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  const readiness = data?.readiness ?? null;
  return (
    <SsnUpdaterDialogView
      open={open}
      resetKey={jobId}
      variant="split"
      jobName={jobName}
      canEdit={canEdit}
      readOnlyReason={readOnlyReason}
      onClose={onClose}
      hasData={!!data}
      loading={status.isLoading || status.isFetching}
      loadError={status.error ?? null}
      check={data?.ssn_check ?? null}
      readiness={readiness}
      blocked={readiness?.updater_blocked ?? data?.ssn_check?.updater_blocked ?? null}
      run={data?.run ?? null}
      onRecheck={handleRecheck}
      onStart={handleStart}
      starting={starting}
      refetch={handleRefetch}
    />
  );
}
